import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, TrendingUp, Clock } from 'lucide-react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Logo from '@/components/Logo';
import { useAuth } from '@/services/auth/authContext';
import { useToast } from '@/components/ui/use-toast';
import SEOHead from '@/components/SEOHead';
import { frictionlessTempApi } from '@/services/api/frictionlessTempApi';

const RestaurantSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();
  
  // Check for frictionless onboarding session
  const sessionId = location.state?.session_id || frictionlessTempApi.getStoredSessionId();
  const restaurantName = location.state?.restaurant_name;
  const hasMenus = location.state?.has_menus;
  
  // Check if coming from rate limit/conversion page
  const fromRateLimit = location.state?.fromRateLimit;
  const extractionComplete = location.state?.extractionComplete;
  const itemsExtracted = location.state?.itemsExtracted;
  
  // Get the intended plan and billing period from URL params
  const intendedPlan = searchParams.get('plan');
  const billingPeriod = searchParams.get('billing');
  
  useEffect(() => {
    // Store session ID in localStorage if we have one
    if (sessionId && !frictionlessTempApi.getStoredSessionId()) {
      frictionlessTempApi.storeSessionId(sessionId);
    }
  }, [sessionId]);
  
  const handleSocialSignup = async (provider: string) => {
    try {
      setIsLoading(true);
      
      // Store the intended plan and billing period if provided
      if (intendedPlan) {
        localStorage.setItem('trial_intended_plan', intendedPlan);
        localStorage.setItem('selected_plan', intendedPlan);
      }
      if (billingPeriod) {
        localStorage.setItem('billing_period', billingPeriod);
      }
      
      // Store session ID to persist through OAuth flow
      if (sessionId) {
        localStorage.setItem('frictionless_session_id', sessionId);
        localStorage.setItem('frictionless_has_menus', hasMenus ? 'true' : 'false');
      }
      
      // CRITICAL: Store extraction state if coming from rate limit page
      if (fromRateLimit && extractionComplete) {
        console.log('📦 Preserving extraction state for OAuth flow:', {
          extractionComplete,
          itemsExtracted,
          sessionData: sessionStorage.getItem('preview_session_token')
        });
        
        // These MUST persist through OAuth redirect
        // Using localStorage because sessionStorage doesn't survive OAuth redirects
        localStorage.setItem('pending_extraction', 'true');
        localStorage.setItem('extraction_items_count', String(itemsExtracted || 0));
        
        // Copy from sessionStorage to localStorage for OAuth persistence
        const sessionToken = sessionStorage.getItem('preview_session_token');
        const cachedExtraction = sessionStorage.getItem('cached_extraction');
        if (sessionToken) {
          localStorage.setItem('extraction_session_token', sessionToken);
        }
        if (cachedExtraction) {
          localStorage.setItem('extraction_was_cached', cachedExtraction);
        }
      }
      
      // Mark this as a signup (not login) for OAuth callback
      localStorage.setItem('oauth_signup_flow', 'true');
      
      // For signup, we use the same login method but will set a flag
      // In real implementation, this would create a new account
      await login(provider);
      
      // After successful signup/login, redirect to plan selection
      const menuMessage = hasMenus 
        ? "Your menu selection has been saved! Let's choose your plan."
        : "Let's choose your plan and set up your restaurant.";
      
      toast({
        title: "Welcome to Dine AI!",
        description: intendedPlan 
          ? `Starting your 30-day trial. You'll be on the ${intendedPlan} plan after trial.`
          : menuMessage,
      });
      
      // Navigate to plan selection for new users - login() method should handle this
      // but we're commenting this out since login() now handles the redirect
      // navigate('/plan-selection?provider=' + provider);
    } catch (error) {
      toast({
        title: "Signup failed",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Start Free Trial | Dine AI Restaurant Signup"
        description="Join restaurants saving 15+ hours weekly and increasing revenue with AI. Start with our Delicious plan 30-day free trial."
        keywords="restaurant signup, free trial, AI restaurant software, start free trial"
      />
      <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
      </div>

      {/* Logo in top-left corner - clickable to home */}
      <Link to="/" className="absolute top-8 left-8 z-20 hover:opacity-80 transition-opacity">
        <Logo className="h-10" />
      </Link>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen pt-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >

          {/* Signup Card with Glass Morphism */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                Let's get started!
              </h1>
              <p className="text-white/80 text-lg mb-6">
                Discover AI-powered restaurant management
              </p>

              {/* Social Proof Badge - Elegant Pill */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-[#FDB913]" />
                <span className="text-[#FDB913] font-medium text-sm">Join 1,000+ restaurants</span>
              </div>
            </div>

            {/* Show menu status if coming from frictionless flow */}
            {hasMenus && (
              <div className="mb-6 p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <Sparkles className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Your menu is ready!</p>
                    <p className="text-white/70 text-sm">
                      {restaurantName ? `${restaurantName}'s menu has been saved` : 'Sign up to continue with your saved menu'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Social Signup Buttons - Full Width Design */}
            <div className="space-y-4">
              {/* Google Signup */}
              <button
                onClick={() => handleSocialSignup('google')}
                disabled={isLoading}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Apple Signup */}
              <button
                onClick={() => handleSocialSignup('apple')}
                disabled={isLoading}
                className="w-full bg-black hover:bg-gray-900 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.96-3.08.44-1.07-.54-2.13-.52-3.25 0-1.44.73-2.21.53-3.1-.44C2.32 14.85 3.07 6.67 8.28 6.31c1.66.07 2.81 1.12 3.72 1.12.89 0 2.37-1.38 4.28-1.17 1.58.12 2.78.73 3.58 1.86-3.25 1.95-2.72 6.21.4 7.47-.95 2.06-2.18 4.1-3.21 4.69zM13.33 5.33c.86-1.1 1.51-2.62 1.28-4.2-1.4.09-3.03.95-3.98 2.13-.86 1.05-1.57 2.61-1.3 4.13 1.53.12 3.01-.83 4-2.06z" />
                </svg>
                Continue with Apple
              </button>
            </div>
          </div>

          {/* Trust Metrics - Outside the card with better spacing */}
          <div className="text-center mt-8">
            <p className="text-white/80 text-base mb-4">
              Built for forward-thinking restaurants
            </p>
            <div className="flex justify-center gap-8">
              <motion.div 
                className="flex items-center gap-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="bg-dineai-yellow/20 p-2 rounded-full">
                  <TrendingUp className="w-4 h-4 text-dineai-yellow" />
                </div>
                <span className="text-white text-sm font-medium">15-30% Revenue Growth</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="bg-dineai-yellow/20 p-2 rounded-full">
                  <Clock className="w-4 h-4 text-dineai-yellow" />
                </div>
                <span className="text-white text-sm font-medium">30-Day ROI</span>
              </motion.div>
            </div>
            <p className="text-white/60 text-sm mt-4">
              Choose your plan • Delicious includes 30-day trial
            </p>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default RestaurantSignup;